<script setup>
import { computed } from 'vue';
import Spinner from 'shared/components/Spinner.vue';

const props = defineProps({
  data: {
    type: Array,
    default: () => [],
  },
  loading: {
    type: Boolean,
    default: false,
  },
  title: {
    type: String,
    default: 'Bot vs Humano por Canal',
  },
});

const filteredData = computed(() => {
  if (!props.data || props.data.length === 0) return [];
  return props.data.filter(i => i.total > 0).sort((a, b) => b.total - a.total);
});

const totals = computed(() => {
  return filteredData.value.reduce(
    (acc, i) => {
      acc.total += i.total;
      acc.bot += i.bot_handled;
      acc.ai += i.ai_handled || 0;
      acc.human += i.human_only;
      return acc;
    },
    { total: 0, bot: 0, ai: 0, human: 0 }
  );
});

const botPercentage = computed(() => {
  if (totals.value.total === 0) return 0;
  return ((totals.value.bot / totals.value.total) * 100).toFixed(1);
});

function getChannelIcon(channelType) {
  const icons = {
    'Channel::WebWidget': 'i-lucide-globe',
    'Channel::Api': 'i-lucide-code',
    'Channel::Whatsapp': 'i-lucide-message-circle',
    'Channel::FacebookPage': 'i-lucide-facebook',
    'Channel::TwitterProfile': 'i-lucide-twitter',
    'Channel::Email': 'i-lucide-mail',
    'Channel::Telegram': 'i-lucide-send',
  };
  return icons[channelType] || 'i-lucide-inbox';
}
</script>

<template>
  <div class="flex flex-col rounded-xl border border-n-weak bg-n-solid-2 p-4">
    <div class="flex items-center gap-2 mb-4">
      <span class="i-lucide-bot text-lg text-n-violet-10" />
      <h3 class="text-base font-semibold text-n-slate-12">{{ title }}</h3>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="flex items-center justify-center h-64">
      <Spinner size="large" />
    </div>

    <!-- Empty State -->
    <div
      v-else-if="filteredData.length === 0"
      class="flex flex-col items-center justify-center h-64 text-n-slate-11"
    >
      <span class="i-lucide-bot text-4xl mb-2 opacity-50" />
      <span class="text-sm">Sem dados de automacao</span>
    </div>

    <div v-else class="flex flex-col gap-4">
      <!-- Summary -->
      <div class="grid grid-cols-3 gap-3">
        <div class="text-center p-3 rounded-lg bg-n-violet-3">
          <div class="text-2xl font-bold text-n-violet-11">
            {{ totals.bot }}
          </div>
          <div class="text-xs text-n-slate-11">Bot</div>
          <div class="text-xs text-n-violet-10">{{ botPercentage }}%</div>
        </div>
        <div class="text-center p-3 rounded-lg bg-n-teal-3">
          <div class="text-2xl font-bold text-n-teal-11">{{ totals.ai }}</div>
          <div class="text-xs text-n-slate-11">AI Agent</div>
        </div>
        <div class="text-center p-3 rounded-lg bg-n-blue-3">
          <div class="text-2xl font-bold text-n-blue-11">
            {{ totals.human }}
          </div>
          <div class="text-xs text-n-slate-11">Humano</div>
        </div>
      </div>

      <!-- By Inbox -->
      <div class="flex flex-col gap-2 max-h-56 overflow-y-auto">
        <div
          v-for="inbox in filteredData"
          :key="inbox.inbox_id"
          class="p-2 rounded-lg bg-n-alpha-1"
        >
          <div class="flex items-center gap-2 mb-2">
            <span
              :class="getChannelIcon(inbox.channel_type)"
              class="text-sm text-n-slate-11"
            />
            <span class="text-sm font-medium text-n-slate-12 flex-1 truncate">{{
              inbox.inbox_name
            }}</span>
            <span class="text-xs text-n-slate-10">{{ inbox.total }} conv.</span>
            <span
              v-if="inbox.has_bot"
              class="px-1.5 py-0.5 text-[10px] rounded bg-n-violet-3 text-n-violet-11"
            >
              Bot
            </span>
            <span
              v-if="inbox.has_ai_agent"
              class="px-1.5 py-0.5 text-[10px] rounded bg-n-teal-3 text-n-teal-11"
            >
              AI
            </span>
          </div>

          <!-- Stacked bar -->
          <div class="flex h-4 rounded overflow-hidden bg-n-alpha-2">
            <div
              v-if="inbox.bot_handled > 0"
              class="bg-n-violet-9 flex items-center justify-center"
              :style="{ width: `${(inbox.bot_handled / inbox.total) * 100}%` }"
              :title="`Bot: ${inbox.bot_handled}`"
            >
              <span
                v-if="inbox.bot_handled / inbox.total > 0.15"
                class="text-[10px] text-white"
              >
                {{ inbox.bot_handled }}
              </span>
            </div>
            <div
              v-if="inbox.ai_handled > 0"
              class="bg-n-teal-9 flex items-center justify-center"
              :style="{ width: `${(inbox.ai_handled / inbox.total) * 100}%` }"
              :title="`AI: ${inbox.ai_handled}`"
            >
              <span
                v-if="inbox.ai_handled / inbox.total > 0.15"
                class="text-[10px] text-white"
              >
                {{ inbox.ai_handled }}
              </span>
            </div>
            <div
              v-if="inbox.human_only > 0"
              class="bg-n-blue-9 flex items-center justify-center"
              :style="{ width: `${(inbox.human_only / inbox.total) * 100}%` }"
              :title="`Humano: ${inbox.human_only}`"
            >
              <span
                v-if="inbox.human_only / inbox.total > 0.15"
                class="text-[10px] text-white"
              >
                {{ inbox.human_only }}
              </span>
            </div>
          </div>
        </div>
      </div>

      <!-- Legend -->
      <div
        class="flex items-center justify-center gap-4 pt-3 border-t border-n-weak text-xs"
      >
        <div class="flex items-center gap-1">
          <span class="w-2 h-2 rounded bg-n-violet-9" />
          <span class="text-n-slate-11">Bot</span>
        </div>
        <div class="flex items-center gap-1">
          <span class="w-2 h-2 rounded bg-n-teal-9" />
          <span class="text-n-slate-11">AI Agent</span>
        </div>
        <div class="flex items-center gap-1">
          <span class="w-2 h-2 rounded bg-n-blue-9" />
          <span class="text-n-slate-11">Humano</span>
        </div>
      </div>
    </div>
  </div>
</template>
